import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Typography,
  Space,
  Card,
  Divider,
  Tag,
  Modal,
  Form,
  message,
  Tooltip,
  Collapse,
  List,
  Badge,
  FloatButton,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ShoppingOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  SearchOutlined,
  SaveOutlined,
  CloseOutlined,
  AppstoreAddOutlined,
} from '@ant-design/icons';
import { EstimateLineItem, Part, Vehicle, LineItemTemplate, PartSavedTemplate } from '../types';
import { lineItemTemplateApi, partSavedTemplateApi, shopSettingsApi } from '../services/api';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

interface MobileServiceItemsTableProps {
  items: EstimateLineItem[];
  onChange: (items: EstimateLineItem[]) => void;
  disabled?: boolean;
  taxRate?: number;
  vehicle?: Vehicle;
}

interface MobileServiceCardProps {
  item: EstimateLineItem;
  onUpdate: (item: EstimateLineItem) => void;
  onDelete: (id: number | string) => void;
  onEdit: () => void;
  isEditing: boolean;
  onSave: () => void;
  onCancel: () => void;
  form: any;
  vehicle?: Vehicle;
  isNarrow?: boolean;
}

const MobileServiceCard: React.FC<MobileServiceCardProps> = ({
  item,
  onUpdate,
  onDelete,
  onEdit,
  isEditing,
  onSave,
  onCancel,
  form,
  vehicle,
  isNarrow = false,
}) => {
  const [partsExpanded, setPartsExpanded] = useState(false);
  const [editingPart, setEditingPart] = useState<string>('');
  const [draftPartValues, setDraftPartValues] = useState<Record<string, Record<string, any>>>({});
  const [partTemplateModalVisible, setPartTemplateModalVisible] = useState(false);
  const [saveTemplateModalVisible, setSaveTemplateModalVisible] = useState(false);
  const [savePartTemplateModalVisible, setSavePartTemplateModalVisible] = useState(false);
  const [templateName, setTemplateName] = useState(item.description || '');
  const [partTemplateName, setPartTemplateName] = useState('');
  const [selectedPartForTemplate, setSelectedPartForTemplate] = useState<Part | null>(null);
  const [templateCategory, setTemplateCategory] = useState(item.category || 'General');
  const [partFilterCategory, setPartFilterCategory] = useState<string>('all');
  const [partSearchText, setPartSearchText] = useState<string>('');
  const [partTemplates, setPartTemplates] = useState<PartSavedTemplate[]>([]);
  const [isGeneral, setIsGeneral] = useState(!vehicle); // Default to general if no vehicle
  const [vehicleYear, setVehicleYear] = useState<number | undefined>();
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');

  // Fetch part templates on mount
  useEffect(() => {
    const fetchPartTemplates = async () => {
      try {
        const response = await partSavedTemplateApi.getAll();
        setPartTemplates(response.data);
      } catch (error) {
        console.error('Error fetching part templates:', error);
      }
    };
    fetchPartTemplates();
  }, []);

  let laborCost = (item.labor_hours || 0) * (item.hourly_rate || 0);
  // Apply labor discount for display
  if (item.labor_discount_percentage && item.labor_discount_percentage > 0) {
    laborCost = laborCost * (1 - item.labor_discount_percentage / 100);
  } else if (item.labor_discount_amount && item.labor_discount_amount > 0) {
    laborCost = laborCost - item.labor_discount_amount;
  }
  laborCost = Math.round(laborCost * 100) / 100;
  const partsCost = (item.parts || []).reduce((sum, part) => sum + (part.total_price || 0), 0);
  const totalCost = laborCost + partsCost;

  const addPart = () => {
    const newPart: Part = {
      id: Date.now(),
      part_number: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      availability: 'in_stock',
    };

    const updatedItem = {
      ...item,
      parts: [...(item.parts || []), newPart]
    };
    onUpdate(updatedItem);
    setEditingPart(newPart.id!.toString());
  };

  const addPartFromTemplate = (template: PartSavedTemplate) => {
    const newPart: Part = {
      id: Date.now(),
      part_number: template.part_number,
      description: template.name,
      quantity: 1,
      unit_price: template.unit_price,
      total_price: template.unit_price,
      availability: template.availability,
      supplier: template.supplier,
    };

    const updatedItem = {
      ...item,
      parts: [...(item.parts || []), newPart]
    };
    onUpdate(updatedItem);
    setPartTemplateModalVisible(false);
    message.success(`Added ${template.name} to service`);
  };

  const updatePart = (partId: string, updatedPart: Partial<Part>) => {
    const updatedParts = (item.parts || []).map(part => {
      if (part.id?.toString() !== partId) return part;
      const merged = { ...part, ...updatedPart };
      let total = (merged.quantity || 1) * (merged.unit_price || 0);
      // Apply part discount
      if (merged.discount_percentage && merged.discount_percentage > 0) {
        total = total * (1 - merged.discount_percentage / 100);
      } else if (merged.discount_amount && merged.discount_amount > 0) {
        total = total - merged.discount_amount;
      }
      merged.total_price = Math.round(total * 100) / 100;
      merged.discounted_price = merged.total_price / (merged.quantity || 1);
      return merged;
    });

    onUpdate({ ...item, parts: updatedParts });
  };

  const deletePart = (partId: string) => {
    const updatedParts = (item.parts || []).filter(part => part.id?.toString() !== partId);
    onUpdate({ ...item, parts: updatedParts });
  };

  const handleSaveAsTemplate = async () => {
    try {
      // Add vehicle information if not marked as general
      const includeVehicle = !isGeneral && vehicle;

      const templateData: Omit<LineItemTemplate, 'id'> = {
        name: templateName,
        category: templateCategory,
        description: item.description,
        quantity: item.quantity,
        labor_hours: item.labor_hours,
        hourly_rate: item.hourly_rate,
        labor_discount_percentage: item.labor_discount_percentage,
        labor_discount_amount: item.labor_discount_amount,
        notes: item.notes,
        vehicle_years: includeVehicle ? vehicle.year.toString() : undefined,
        vehicle_make: includeVehicle ? vehicle.make : undefined,
        vehicle_model: includeVehicle ? vehicle.model : undefined,
      };

      await lineItemTemplateApi.create(templateData);
      message.success(`Template "${templateName}" created successfully`);
      setSaveTemplateModalVisible(false);
    } catch (error) {
      console.error('Error saving template:', error);
      message.error('Failed to save template');
    }
  };

  const handleSavePartAsTemplate = async () => {
    if (!selectedPartForTemplate) return;

    try {
      // Add vehicle information if not marked as general
      const includeVehicle = !isGeneral && vehicle;

      const templateData: Omit<PartSavedTemplate, 'id'> = {
        part_number: selectedPartForTemplate.part_number,
        name: partTemplateName,
        description: selectedPartForTemplate.description,
        unit_price: selectedPartForTemplate.unit_price,
        discount_percentage: selectedPartForTemplate.discount_percentage,
        discount_amount: selectedPartForTemplate.discount_amount,
        availability: selectedPartForTemplate.availability,
        supplier: selectedPartForTemplate.supplier,
        notes: selectedPartForTemplate.notes,
        vehicle_years: includeVehicle ? vehicle.year.toString() : undefined,
        vehicle_make: includeVehicle ? vehicle.make : undefined,
        vehicle_model: includeVehicle ? vehicle.model : undefined,
      };

      const response = await partSavedTemplateApi.create(templateData);
      setPartTemplates([...partTemplates, response.data]); // Add to local state
      message.success(`Part template "${partTemplateName}" created successfully`);
      setSavePartTemplateModalVisible(false);
      setSelectedPartForTemplate(null);
      setPartTemplateName('');
    } catch (error) {
      console.error('Error saving part template:', error);
      message.error('Failed to save part template');
    }
  };

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'in_stock': return '#52c41a';
      case 'order_required': return '#faad14';
      case 'backordered': return '#f5222d';
      case 'discontinued': return '#8c8c8c';
      default: return '#d9d9d9';
    }
  };

  const getAvailabilityText = (availability: string) => {
    switch (availability) {
      case 'in_stock': return 'In Stock';
      case 'order_required': return 'Order Required';
      case 'backordered': return 'Backordered';
      case 'discontinued': return 'Discontinued';
      default: return availability;
    }
  };

  const getFilteredPartTemplates = (): PartSavedTemplate[] => {
    return partTemplates.filter(template => {
      // Filter by search text
      if (partSearchText) {
        const searchLower = partSearchText.toLowerCase();
        const matchesSearch =
          template.name.toLowerCase().includes(searchLower) ||
          template.part_number.toLowerCase().includes(searchLower) ||
          template.description.toLowerCase().includes(searchLower) ||
          (template.supplier && template.supplier.toLowerCase().includes(searchLower));

        if (!matchesSearch) return false;
      }

      // Filter by category - skip for now since PartSavedTemplate doesn't have category in the same way
      // if (partFilterCategory !== 'all' && template.category !== partFilterCategory) {
      //   return false;
      // }

      // Show general templates always
      if (!template.vehicle_years && !template.vehicle_make && !template.vehicle_model) {
        return true;
      }

      // Filter by vehicle if template is vehicle-specific
      if (vehicle) {
        const years = template.vehicle_years ? template.vehicle_years.split(',').map(y => parseInt(y.trim())) : [];
        const matchesVehicle =
          years.includes(vehicle.year) &&
          template.vehicle_make === vehicle.make &&
          template.vehicle_model === vehicle.model;
        return matchesVehicle;
      }

      return true;
    });
  };

  return (
    <Card
      style={{
        marginBottom: '16px',
        border: isEditing ? '2px solid #1890ff' : '1px solid #d9d9d9',
        borderRadius: '8px',
        overflow: isEditing ? 'visible' : 'hidden'
      }}
      bodyStyle={{ padding: '16px' }}
    >
      {/* Service Header */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div style={{ flex: 1, marginRight: '8px' }}>
            {isEditing ? (
              <Form.Item
                name="description"
                style={{ margin: 0, marginBottom: '8px' }}
                rules={[{ required: true, message: 'Description is required' }]}
              >
                <Input placeholder="Service description" size="large" />
              </Form.Item>
            ) : (
              <Title level={5} style={{ margin: 0, marginBottom: '4px', fontSize: '16px' }}>
                {item.description}
              </Title>
            )}

            {!isEditing && (
              <Tag color="blue" style={{ fontSize: '11px' }}>{item.category}</Tag>
            )}
            {!isEditing && item.notes && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {item.notes}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '4px' }}>
            {isEditing ? (
              <Button size="small" icon={<CloseOutlined />} onClick={onCancel}>
                Cancel
              </Button>
            ) : (
              <>
                <Button type="text" size="small" icon={<SaveOutlined />} onClick={() => setSaveTemplateModalVisible(true)}>
                  Save
                </Button>
                <Button type="text" size="small" icon={<EditOutlined />} onClick={onEdit}>
                  Edit
                </Button>
                <Popconfirm
                  title="Delete this service?"
                  description="This will remove the service and all its parts."
                  onConfirm={() => onDelete(item.id || 'new')}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                </Popconfirm>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Service Details */}
      <div style={{ marginBottom: '16px' }}>
        {isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', gap: '8px' }}>
              <Form.Item name="quantity" label="Quantity" style={{ margin: 0 }}>
                <InputNumber min={1} style={{ width: '100%' }} size="large" />
              </Form.Item>
              <Form.Item name="labor_hours" label="Labor Hours" style={{ margin: 0 }}>
                <InputNumber min={0} step={0.1} precision={1} style={{ width: '100%' }} size="large" />
              </Form.Item>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', gap: '8px' }}>
              <Form.Item name="hourly_rate" label="Hourly Rate" style={{ margin: 0 }}>
                <InputNumber min={0} step={5} style={{ width: '100%' }} prefix="$" size="large" />
              </Form.Item>
              <div style={{ display: 'flex', alignItems: 'end' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Labor Cost</Text>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>${laborCost.toFixed(2)}</div>
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Form.Item name="labor_discount_percentage" label="Labor Disc %" style={{ margin: 0 }}>
                <InputNumber
                  min={0}
                  max={100}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="0"
                  inputMode="decimal"
                />
              </Form.Item>
              <Form.Item name="labor_discount_amount" label="Labor Disc $" style={{ margin: 0 }}>
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  inputMode="decimal"
                />
              </Form.Item>
            </div>
            {/* Save/Cancel at bottom of edit form */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <Button type="primary" size="large" icon={<CheckCircleOutlined />} onClick={onSave} block>
                Save Service
              </Button>
              <Button size="large" icon={<CloseOutlined />} onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', textAlign: 'center' }}>
              <div>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Quantity</Text>
                <Text strong>{item.quantity}</Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Labor</Text>
                <Text strong>{item.labor_hours ? `${item.labor_hours}h @ $${item.hourly_rate}/hr` : 'No labor'}</Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Labor Cost</Text>
                <Text strong style={{ color: '#1890ff' }}>${laborCost.toFixed(2)}</Text>
              </div>
            </div>
            {/* Show labor discount if applied */}
            {(item.labor_discount_percentage || item.labor_discount_amount) && (() => {
              const originalLabor = (item.labor_hours || 0) * (item.hourly_rate || 0);
              const savedAmount = Math.round((originalLabor - laborCost) * 100) / 100;
              return (
                <div style={{ marginTop: 8, padding: '6px 10px', background: '#f0fdf4', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text style={{ color: '#16a34a', fontSize: 13, fontWeight: 500 }}>
                      {item.labor_discount_percentage ? `${item.labor_discount_percentage}% off` : `$${(item.labor_discount_amount || 0).toFixed(2)} off`}
                    </Text>
                    <Text style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>
                      <span style={{ textDecoration: 'line-through' }}>${originalLabor.toFixed(2)}</span>
                      {' → '}
                      <span style={{ color: '#16a34a', fontWeight: 600 }}>${laborCost.toFixed(2)}</span>
                      <span style={{ color: '#16a34a' }}> (saved ${savedAmount.toFixed(2)})</span>
                    </Text>
                  </div>
                  <Button type="link" size="small" onClick={onEdit} style={{ padding: 0, fontSize: 12 }}>Edit</Button>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Parts Section */}
      <Divider style={{ margin: '16px 0' }} />

      <div style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <ShoppingOutlined style={{ color: '#52c41a' }} />
            <Text strong>Parts</Text>
            <Badge count={item.parts?.length || 0} size="small" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr 1fr', gap: '8px' }}>
            <Button
              type="default"
              size="small"
              icon={<SearchOutlined />}
              onClick={() => {
                window.open('https://app.partstech.com', '_blank', 'noopener,noreferrer');
              }}
              style={{ fontSize: '12px', color: '#1890ff', borderColor: '#1890ff' }}
            >
              PartsTech
            </Button>
            <Button
              type="primary"
              size="small"
              icon={<AppstoreAddOutlined />}
              onClick={() => setPartTemplateModalVisible(true)}
              style={{ fontSize: '12px' }}
            >
              Template
            </Button>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={addPart}
              style={{ fontSize: '12px' }}
            >
              New
            </Button>
          </div>
        </div>

        {item.parts && item.parts.length > 0 ? (
          <List
            dataSource={item.parts}
            renderItem={(part) => (
              <List.Item style={{ padding: '6px 0', border: 'none' }}>
                <Card
                  size="small"
                  style={{ width: '100%', backgroundColor: '#fafafa', overflow: 'hidden' }}
                  bodyStyle={{ padding: '12px' }}
                >
                  {editingPart === part.id?.toString() ? (
                    <div style={{ overflow: 'hidden' }}>
                      <Input
                        placeholder="Part description"
                        value={draftPartValues[part.id!]?.description ?? part.description}
                        onChange={(e) => setDraftPartValues(prev => ({
                          ...prev,
                          [part.id!]: { ...prev[part.id!], description: e.target.value }
                        }))}
                        onBlur={() => {
                          const draft = draftPartValues[part.id!];
                          if (draft?.description !== undefined) {
                            updatePart(part.id!.toString(), { description: draft.description });
                            setDraftPartValues(prev => { const n = { ...prev }; delete n[part.id!]; return n; });
                          }
                        }}
                        style={{ marginBottom: '8px' }}
                        size="small"
                      />
                      <Input
                        placeholder="Part number"
                        value={draftPartValues[part.id!]?.part_number ?? part.part_number}
                        onChange={(e) => setDraftPartValues(prev => ({
                          ...prev,
                          [part.id!]: { ...prev[part.id!], part_number: e.target.value }
                        }))}
                        onBlur={() => {
                          const draft = draftPartValues[part.id!];
                          if (draft?.part_number !== undefined) {
                            updatePart(part.id!.toString(), { part_number: draft.part_number });
                            setDraftPartValues(prev => { const n = { ...prev }; delete n[part.id!]; return n; });
                          }
                        }}
                        style={{ marginBottom: '8px' }}
                        size="small"
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                          <InputNumber
                            placeholder="Qty"
                            min={1}
                            value={draftPartValues[part.id!]?.quantity ?? part.quantity}
                            onChange={(value) => setDraftPartValues(prev => ({
                              ...prev,
                              [part.id!]: { ...prev[part.id!], quantity: value ?? 1 }
                            }))}
                            onBlur={() => {
                              const draft = draftPartValues[part.id!];
                              if (draft?.quantity !== undefined) {
                                updatePart(part.id!.toString(), { quantity: draft.quantity || 1 });
                                setDraftPartValues(prev => { const n = { ...prev }; delete n[part.id!]; return n; });
                              }
                            }}
                            style={{ width: '100%' }}
                            size="small"
                          />
                          <InputNumber
                            placeholder="Price"
                            min={0}
                            step={0.01}
                            precision={2}
                            value={draftPartValues[part.id!]?.unit_price ?? part.unit_price}
                            onChange={(value) => setDraftPartValues(prev => ({
                              ...prev,
                              [part.id!]: { ...prev[part.id!], unit_price: value ?? 0 }
                            }))}
                            onBlur={() => {
                              const draft = draftPartValues[part.id!];
                              if (draft?.unit_price !== undefined) {
                                updatePart(part.id!.toString(), { unit_price: draft.unit_price || 0 });
                                setDraftPartValues(prev => { const n = { ...prev }; delete n[part.id!]; return n; });
                              }
                            }}
                            style={{ width: '100%' }}
                            prefix="$"
                            size="small"
                          />
                        </div>
                        <Select
                          value={part.availability}
                          onChange={(value) => updatePart(part.id!.toString(), { availability: value })}
                          size="small"
                          style={{ width: '100%' }}
                        >
                          <Select.Option value="in_stock">In Stock</Select.Option>
                          <Select.Option value="order_required">Order Required</Select.Option>
                          <Select.Option value="backordered">Backordered</Select.Option>
                          <Select.Option value="discontinued">Discontinued</Select.Option>
                        </Select>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: '8px' }}>
                        <div>
                          <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: 2 }}>Disc %</Text>
                          <InputNumber
                            min={0}
                            max={100}
                            step={1}
                            style={{ width: '100%' }}
                            placeholder="0"
                            inputMode="decimal"
                            size="small"
                            value={part.discount_percentage}
                            onChange={(value) => updatePart(part.id!.toString(), { discount_percentage: value || 0 })}
                          />
                        </div>
                        <div>
                          <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: 2 }}>Disc $</Text>
                          <InputNumber
                            min={0}
                            step={0.01}
                            style={{ width: '100%' }}
                            placeholder="0.00"
                            inputMode="decimal"
                            size="small"
                            value={part.discount_amount}
                            onChange={(value) => updatePart(part.id!.toString(), { discount_amount: value || 0 })}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <Text strong style={{ fontSize: '14px' }}>Total: ${part.total_price.toFixed(2)}</Text>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <Button
                            type="primary"
                            size="small"
                            icon={<CheckCircleOutlined />}
                            onClick={() => setEditingPart('')}
                          >
                            Save
                          </Button>
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => deletePart(part.id!.toString())}
                            danger
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ overflow: 'hidden' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text strong style={{ fontSize: '14px', display: 'block', wordBreak: 'break-word' }}>{part.description}</Text>
                          <Text type="secondary" style={{ fontSize: '12px', wordBreak: 'break-word' }}>#{part.part_number}</Text>
                          {part.vendorSearchLinks && part.vendorSearchLinks.length > 0 && (
                            <div style={{ fontSize: '11px', marginTop: '3px' }}>
                              <Text type="secondary" style={{ fontSize: '11px' }}>Search: </Text>
                              {part.vendorSearchLinks.map((link, idx) => (
                                <span key={link.vendor}>
                                  {idx > 0 && <Text type="secondary" style={{ fontSize: '11px' }}> </Text>}
                                  <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: '11px' }}
                                  >
                                    {link.vendor}
                                  </a>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                          <Button
                            type="text"
                            size="small"
                            icon={<SaveOutlined />}
                            onClick={() => {
                              setSelectedPartForTemplate(part);
                              setPartTemplateName(part.description);
                              setSavePartTemplateModalVisible(true);
                            }}
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => setEditingPart(part.id!.toString())}
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => deletePart(part.id!.toString())}
                            danger
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <Text style={{ fontSize: '12px' }}>Qty: {part.quantity}</Text>
                          <Text style={{ fontSize: '12px' }}>${part.unit_price} each</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                          <Tag color={getAvailabilityColor(part.availability)} style={{ margin: 0, fontSize: '10px' }}>
                            {getAvailabilityText(part.availability)}
                          </Tag>
                          {(part.discount_percentage || part.discount_amount) ? (
                            <div style={{ textAlign: 'right' }}>
                              <Text style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: 12 }}>${((part.quantity || 1) * (part.unit_price || 0)).toFixed(2)}</Text>
                              <Text strong style={{ color: '#16a34a', whiteSpace: 'nowrap', marginLeft: 4 }}>${part.total_price.toFixed(2)}</Text>
                            </div>
                          ) : (
                            <Text strong style={{ color: '#52c41a', whiteSpace: 'nowrap' }}>${part.total_price.toFixed(2)}</Text>
                          )}
                        </div>
                      </div>
                      {/* Show part discount badge */}
                      {(part.discount_percentage || part.discount_amount) && (
                        <div style={{ marginTop: 4, padding: '2px 8px', background: '#f0fdf4', borderRadius: 4 }}>
                          <Text style={{ color: '#16a34a', fontSize: 11 }}>
                            {part.discount_percentage ? `${part.discount_percentage}% off` : `$${(part.discount_amount || 0).toFixed(2)} off`}
                            {' — saved $'}
                            {(((part.quantity || 1) * (part.unit_price || 0)) - part.total_price).toFixed(2)}
                          </Text>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              </List.Item>
            )}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '16px', color: '#999' }}>
            <ShoppingOutlined style={{ fontSize: '24px', marginBottom: '8px', display: 'block' }} />
            <Text type="secondary">No parts added yet</Text>
          </div>
        )}
      </div>

      {/* Total Section */}
      <Divider style={{ margin: '16px 0' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
        <div>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Service Total</Text>
          <div style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
            <Text>Labor: ${laborCost.toFixed(2)}</Text>
            <Text>Parts: ${partsCost.toFixed(2)}</Text>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
            ${totalCost.toFixed(2)}
          </Text>
        </div>
      </div>

      {/* Part Template Modal */}
      <Modal
        title="Select Part Template"
        open={partTemplateModalVisible}
        onCancel={() => {
          setPartTemplateModalVisible(false);
          setPartSearchText('');
        }}
        footer={null}
        width="95%"
        style={{ top: 20 }}
        bodyStyle={{ padding: '16px' }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder="Search parts..."
            value={partSearchText}
            onChange={(e) => setPartSearchText(e.target.value)}
            style={{ width: '100%', fontSize: '16px' }}
            prefix={<SearchOutlined />}
            allowClear
            size="large"
          />
        </div>

        {getFilteredPartTemplates().length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <ShoppingOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
            <Text type="secondary" style={{ display: 'block' }}>No templates found</Text>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
              Create templates by clicking the Save button on any part
            </Text>
          </div>
        ) : (
          <List
            dataSource={getFilteredPartTemplates()}
            renderItem={(template: PartSavedTemplate) => (
              <List.Item>
                <Card
                  hoverable
                  style={{ width: '100%' }}
                  onClick={() => addPartFromTemplate(template)}
                  bodyStyle={{ padding: '16px' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <Title level={5} style={{ margin: 0, marginBottom: '4px' }}>
                        {template.name}
                      </Title>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                        #{template.part_number}
                      </Text>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Tag color={getAvailabilityColor(template.availability)} style={{ fontSize: '10px' }}>
                          {getAvailabilityText(template.availability)}
                        </Tag>
                        {template.supplier && (
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            {template.supplier}
                          </Text>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                        ${template.unit_price.toFixed(2)}
                      </Text>
                    </div>
                  </div>
                </Card>
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* Save as Template Modal */}
      <Modal
        title="Save as Service Template"
        open={saveTemplateModalVisible}
        onCancel={() => setSaveTemplateModalVisible(false)}
        onOk={handleSaveAsTemplate}
        okText="Save Template"
        cancelText="Cancel"
        width="90%"
      >
        <div style={{ padding: '12px 0' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
            Save this service configuration as a reusable template.
          </Text>

          <Form layout="vertical">
            <Form.Item label="Template Name" required>
              <Input
                placeholder="Enter template name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                size="large"
              />
            </Form.Item>

            <Form.Item label="Category" required>
              <Select
                value={templateCategory}
                onChange={setTemplateCategory}
                size="large"
              >
                <Select.Option value="General">General</Select.Option>
                <Select.Option value="Engine">Engine</Select.Option>
                <Select.Option value="Transmission">Transmission</Select.Option>
                <Select.Option value="Brakes">Brakes</Select.Option>
                <Select.Option value="Suspension">Suspension</Select.Option>
                <Select.Option value="Electrical">Electrical</Select.Option>
                <Select.Option value="AC/Heating">AC/Heating</Select.Option>
                <Select.Option value="Body">Body</Select.Option>
                <Select.Option value="Maintenance">Maintenance</Select.Option>
                <Select.Option value="Diagnostics">Diagnostics</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item help={!vehicle ? undefined : "If unchecked, this template will only appear when working with similar vehicles"}>
              <Checkbox
                checked={isGeneral}
                onChange={(e) => setIsGeneral(e.target.checked)}
              >
                Make this a general template (available for all vehicles)
              </Checkbox>
            </Form.Item>
          </Form>
        </div>
      </Modal>

      {/* Save Part as Template Modal */}
      <Modal
        title="Save as Part Template"
        open={savePartTemplateModalVisible}
        onCancel={() => {
          setSavePartTemplateModalVisible(false);
          setSelectedPartForTemplate(null);
          setPartTemplateName('');
        }}
        onOk={handleSavePartAsTemplate}
        okText="Save Template"
        cancelText="Cancel"
        width="90%"
      >
        <div style={{ padding: '12px 0' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
            Save this part configuration as a reusable template.
          </Text>

          <Form layout="vertical">
            <Form.Item label="Template Name" required>
              <Input
                placeholder="Enter template name"
                value={partTemplateName}
                onChange={(e) => setPartTemplateName(e.target.value)}
                size="large"
              />
            </Form.Item>

            <Form.Item label="Category" required>
              <Select
                value={templateCategory}
                onChange={setTemplateCategory}
                size="large"
              >
                <Select.Option value="General">General</Select.Option>
                <Select.Option value="Engine">Engine</Select.Option>
                <Select.Option value="Transmission">Transmission</Select.Option>
                <Select.Option value="Brakes">Brakes</Select.Option>
                <Select.Option value="Suspension">Suspension</Select.Option>
                <Select.Option value="Electrical">Electrical</Select.Option>
                <Select.Option value="Fluids">Fluids</Select.Option>
                <Select.Option value="Filters">Filters</Select.Option>
                <Select.Option value="Body">Body</Select.Option>
                <Select.Option value="Interior">Interior</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item help={!vehicle ? undefined : "If unchecked, this template will only appear when working with similar vehicles"}>
              <Checkbox
                checked={isGeneral}
                onChange={(e) => setIsGeneral(e.target.checked)}
              >
                Make this a general template (available for all vehicles)
              </Checkbox>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </Card>
  );
};

const MobileServiceItemsTable: React.FC<MobileServiceItemsTableProps> = ({
  items,
  onChange,
  disabled = false,
  taxRate = 7,
  vehicle,
}) => {
  const [editingId, setEditingId] = useState<string>('');
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [lineItemTemplates, setLineItemTemplates] = useState<LineItemTemplate[]>([]);
  const [defaultLaborRate, setDefaultLaborRate] = useState<number>(175);
  const [mobilePartsMarkup] = useState(40);

  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 400);
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Track narrow screens for single-column grid
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => setIsNarrow(window.innerWidth < 400), 250);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);

  // Fetch line item templates and shop settings from database
  useEffect(() => {
    const fetchLineItemTemplates = async () => {
      try {
        const response = await lineItemTemplateApi.getAll();
        setLineItemTemplates(response.data);
      } catch (error) {
        console.error('Error fetching line item templates:', error);
      }
    };
    const fetchShopSettings = async () => {
      try {
        const response = await shopSettingsApi.get();
        if (response.data.labor_rate) {
          setDefaultLaborRate(response.data.labor_rate);
        }
      } catch (error) {
        console.error('Error fetching shop settings:', error);
      }
    };
    fetchLineItemTemplates();
    fetchShopSettings();
  }, []);

  // Filter templates based on category and vehicle
  const getFilteredServiceTemplates = (): LineItemTemplate[] => {
    return lineItemTemplates.filter(template => {
      // Filter by search text
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const matchesSearch =
          template.name.toLowerCase().includes(searchLower) ||
          template.description.toLowerCase().includes(searchLower) ||
          template.category.toLowerCase().includes(searchLower);

        if (!matchesSearch) return false;
      }

      // Filter by category
      if (filterCategory !== 'all' && template.category !== filterCategory) {
        return false;
      }

      // Show general templates always (no vehicle info)
      if (!template.vehicle_years && !template.vehicle_make && !template.vehicle_model) {
        return true;
      }

      // Filter by vehicle if template is vehicle-specific
      if (vehicle) {
        const years = template.vehicle_years ? template.vehicle_years.split(',').map(y => parseInt(y.trim())) : [];
        const matchesVehicle =
          years.includes(vehicle.year) &&
          template.vehicle_make === vehicle.make &&
          template.vehicle_model === vehicle.model;
        return matchesVehicle;
      }

      return true;
    });
  };

  const addNewService = () => {
    const newItem: EstimateLineItem = {
      id: Date.now(),
      category: 'Custom',
      description: '',
      quantity: 1,
      labor_hours: 0,
      hourly_rate: defaultLaborRate,
      total_price: 0,
      parts: [],
    };

    onChange([...items, newItem]);
    editService(newItem);
  };


  const addFromTemplate = (template: LineItemTemplate) => {
    const hourlyRate = template.hourly_rate || defaultLaborRate;
    const laborCost = (template.labor_hours || 0) * hourlyRate;
    const newItem: EstimateLineItem = {
      id: Date.now(),
      category: template.category,
      description: template.name,
      quantity: template.quantity,
      labor_hours: template.labor_hours,
      hourly_rate: hourlyRate,
      labor_discount_percentage: template.labor_discount_percentage,
      labor_discount_amount: template.labor_discount_amount,
      total_price: laborCost,
      parts: [],
      notes: template.notes,
    };

    onChange([...items, newItem]);
    setTemplateModalVisible(false);
    message.success(`Added ${template.name} to estimate`);
  };

  const editService = (item: EstimateLineItem) => {
    form.setFieldsValue({
      description: item.description,
      quantity: item.quantity,
      labor_hours: item.labor_hours,
      hourly_rate: item.hourly_rate,
      category: item.category,
      labor_discount_percentage: item.labor_discount_percentage,
      labor_discount_amount: item.labor_discount_amount,
    });
    setEditingId((item.id || 'new').toString());
  };

  const saveService = async () => {
    try {
      const values = await form.validateFields();
      const updatedItems = items.map(item => {
        if ((item.id || 'new').toString() === editingId) {
          let laborCost = (values.labor_hours || 0) * (values.hourly_rate || 0);

          // Apply labor discount
          if (values.labor_discount_percentage && values.labor_discount_percentage > 0) {
            laborCost = laborCost * (1 - values.labor_discount_percentage / 100);
          } else if (values.labor_discount_amount && values.labor_discount_amount > 0) {
            laborCost = laborCost - values.labor_discount_amount;
          }
          laborCost = Math.round(laborCost * 100) / 100;

          const partsCost = (item.parts || []).reduce((sum, part) => {
            let partTotal = (part.quantity || 1) * (part.unit_price || 0);
            // Apply part discount
            if (part.discount_percentage && part.discount_percentage > 0) {
              partTotal = partTotal * (1 - part.discount_percentage / 100);
            } else if (part.discount_amount && part.discount_amount > 0) {
              partTotal = partTotal - part.discount_amount;
            }
            return sum + Math.round(partTotal * 100) / 100;
          }, 0);

          return {
            ...item,
            ...values,
            total_price: laborCost + partsCost,
          };
        }
        return item;
      });

      onChange(updatedItems);
      setEditingId('');
      form.resetFields();
    } catch (error) {
      console.log('Validation failed:', error);
    }
  };

  const cancelEdit = () => {
    setEditingId('');
    form.resetFields();
  };

  const deleteService = (id: number | string) => {
    const filteredItems = items.filter(item => (item.id || 'new').toString() !== id.toString());
    onChange(filteredItems);
    if (editingId === id.toString()) {
      setEditingId('');
      form.resetFields();
    }
  };

  const updateService = (updatedItem: EstimateLineItem) => {
    const updatedItems = items.map(item =>
      item.id === updatedItem.id ? updatedItem : item
    );
    onChange(updatedItems);
  };

  return (
    <div>
      <Form form={form}>
        {/* Services List */}
        <div style={{ marginBottom: '16px' }}>
          {items.length > 0 ? (
            items.map((item) => {
              return (
                <React.Fragment key={item.id || 'new'}>
                  <MobileServiceCard
                    item={item}
                    onUpdate={updateService}
                    onDelete={deleteService}
                    onEdit={() => editService(item)}
                    isEditing={editingId === (item.id || 'new').toString()}
                    onSave={saveService}
                    onCancel={cancelEdit}
                    form={form}
                    vehicle={vehicle}
                    isNarrow={isNarrow}
                  />
                </React.Fragment>
              );
            })
          ) : (
            <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
              <ToolOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px', display: 'block' }} />
              <Title level={4} type="secondary">No Services Added Yet</Title>
              <Text type="secondary" style={{ marginBottom: '20px', display: 'block' }}>
                Add services from templates or create custom services
              </Text>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <Button
                    type="primary"
                    icon={<AppstoreAddOutlined />}
                    onClick={() => setTemplateModalVisible(true)}
                    size="large"
                    style={{ flex: 1 }}
                  >
                    Template
                  </Button>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={addNewService}
                    size="large"
                    style={{ flex: 1 }}
                  >
                    Custom
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>

      </Form>

      {/* Service Template Modal */}
      <Modal
        title="Select Service Template"
        open={templateModalVisible}
        onCancel={() => {
          setTemplateModalVisible(false);
          setSearchText('');
        }}
        footer={null}
        width="95%"
        style={{ top: 20 }}
        bodyStyle={{ padding: '16px' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
          <Input
            placeholder="Search services..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: '100%', fontSize: '16px' }}
            prefix={<SearchOutlined />}
            allowClear
            size="large"
          />
          <Select
            placeholder="Filter by category"
            value={filterCategory}
            onChange={setFilterCategory}
            style={{ width: '100%' }}
            size="large"
          >
            <Select.Option value="all">All Categories</Select.Option>
            <Select.Option value="General">General</Select.Option>
            <Select.Option value="Engine">Engine</Select.Option>
            <Select.Option value="Transmission">Transmission</Select.Option>
            <Select.Option value="Brakes">Brakes</Select.Option>
            <Select.Option value="Suspension">Suspension</Select.Option>
            <Select.Option value="Electrical">Electrical</Select.Option>
            <Select.Option value="AC/Heating">AC/Heating</Select.Option>
            <Select.Option value="Body">Body</Select.Option>
            <Select.Option value="Maintenance">Maintenance</Select.Option>
            <Select.Option value="Diagnostics">Diagnostics</Select.Option>
          </Select>
        </div>

        {getFilteredServiceTemplates().length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <ToolOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
            <Text type="secondary" style={{ display: 'block' }}>No templates found</Text>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
              Create templates by clicking the Save button on any service
            </Text>
          </div>
        ) : (
          <List
            dataSource={getFilteredServiceTemplates()}
            renderItem={(template: LineItemTemplate) => {
              const laborCost = (template.labor_hours || 0) * (template.hourly_rate || defaultLaborRate);
              return (
                <List.Item>
                  <Card
                    hoverable
                    style={{ width: '100%' }}
                    onClick={() => addFromTemplate(template)}
                    bodyStyle={{ padding: '16px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <Title level={5} style={{ margin: 0, marginBottom: '4px' }}>
                          {template.name}
                        </Title>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                          {template.description}
                        </Text>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <Tag color="blue">{template.category}</Tag>
                          {template.labor_hours && (
                            <Text style={{ fontSize: '12px' }}>
                              {template.labor_hours}h @ ${template.hourly_rate || defaultLaborRate}/hr
                            </Text>
                          )}
                          {template.vehicle_years && (
                            <Tag color="green" style={{ fontSize: '10px' }}>
                              {template.vehicle_make} {template.vehicle_model}
                            </Tag>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                          ${laborCost.toFixed(2)}
                        </Text>
                      </div>
                    </div>
                  </Card>
                </List.Item>
              );
            }}
          />
        )}
      </Modal>

      {/* Inline add buttons after items exist */}
      {items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              icon={<AppstoreAddOutlined />}
              onClick={() => setTemplateModalVisible(true)}
              style={{ flex: 1, height: 44, fontWeight: 600 }}
            >
              Template
            </Button>
            <Button
              icon={<PlusOutlined />}
              onClick={addNewService}
              style={{ flex: 1, height: 44, fontWeight: 600 }}
            >
              Custom
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileServiceItemsTable;